import { useState, useEffect } from 'react';
import eadoCivicLogo from '../assets/img/eado_civic_logo_v2-noBackground.png';
import eadoBoundaryMap from '../assets/img/eado_boundaries_2.png';
import districtIMap from '../assets/img/district_i.png';
import MailingListModal from './MailingListModal';
import './Home.css';

function Home() {
  const [showHeader, setShowHeader] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [events, setEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [showMailingModal, setShowMailingModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowHeader(true);
      } else {
        setShowHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch highlighted events from our secure serverless function
        const response = await fetch('/api/calendar/events');

        if (response.ok) {
          const highlightedEvents = await response.json();
          setEvents(highlightedEvents);
        } else {
          console.error('Calendar API Error:', response.status);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };

    fetchEvents();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  return (
    <div className="App">
      {/* Sticky Header */}
      <header className={`sticky-header ${showHeader ? 'visible' : ''}`}>
        <div className="header-content">
          <img src={eadoCivicLogo} className="header-logo" alt="Eado Civic logo" onClick={() => scrollToSection('hero')} />
          <button className={`menu-toggle ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        <nav className={`nav-menu ${menuOpen ? 'open' : ''}`}>
          <button onClick={() => scrollToSection('about')}>About</button>
          <button onClick={() => scrollToSection('events')}>Upcoming Events</button>
          <button onClick={() => scrollToSection('resources')}>City Resources</button>
        </nav>
      </header>

      {/* Hero Section */}
      <div id="hero" className="hero">
        <div className="hero-content">
          <img src={eadoCivicLogo} className="hero-logo" alt="Eado Civic logo" />
          <h2 className="hero-tagline">Building Community Together</h2>
          <p className="hero-subtitle">Join us in making Eado a better place to live, work, and connect</p>
          <div className="social-links">
            <a
              href="https://www.instagram.com/EadoCivic/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
              aria-label="Follow us on Instagram"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=100091404527985"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
              aria-label="Follow us on Facebook"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a
              href="https://chat.whatsapp.com/IadvuPHwjuqKQSfgkeDAjp"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon"
              aria-label="Join our WhatsApp group"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </a>
          </div>
          <div className="cta-buttons">
            <button
              className="cta-button"
              onClick={() => setShowMailingModal(true)}
            >
              Join Our Mailing List
            </button>
            <a
              href="https://forms.gle/fxUnkuAEvR5eA5hG7"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
            >
              Take the EaDo Community Survey
            </a>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div id="about" className="about">
        <div className="about-content">
          <h2 className="section-title">About EaDo Civic</h2>
          <p className="section-text">
            EaDo Civic is a resident-focused organization dedicated to enhancing the quality of life
            in Houston's East Downtown neighborhood. We bring together residents, business owners,
            and community leaders to create positive change through collaboration and civic engagement.
          </p>
          <p className="section-text">
            Our vibrant neighborhood is known for its rich history, diverse culture, and strong
            sense of community. Through regular meetings, community events, and local initiatives,
            we work to preserve what makes EaDo special while building a brighter future for all
            who call it home.
          </p>
          <div className="about-highlights">
            <div className="highlight-card">
              <h3>Our Mission</h3>
              <p>Empowering residents to actively participate in shaping our neighborhood's future</p>
            </div>
            <div className="highlight-card">
              <h3>Our Vision</h3>
              <p>A thriving, inclusive community where everyone has a voice and opportunity to contribute</p>
            </div>
            <div className="highlight-card">
              <h3>Our Values</h3>
              <p>Collaboration, transparency, and dedication to the people who make EaDo home</p>
            </div>
          </div>
          <div className="about-map-section">
            <h3 className="about-map-title">Neighborhood Boundaries</h3>
            <div className="about-map-container">
              <img
                src={eadoBoundaryMap}
                alt="East Downtown Neighborhood Boundaries"
                className="about-map-image"
                onClick={() => setFullscreenImage({ src: eadoBoundaryMap, alt: 'East Downtown Neighborhood Boundaries' })}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Events Section */}
      <div id="events" className="events">
        <div className="events-content">
          <h2 className="section-title">Upcoming events</h2>
          <p className="section-text">Stay informed about upcoming events</p>

          <div className="events-grid">
            {events.length === 0 ? (
              <div className="events-card">
                <div className="events-title">Community Meeting</div>
                <p><strong>Last monday of every month at 6:30 PM</strong></p>
                <p>
                  Join us for our community meeting to discuss upcoming
                  projects and neighborhood concerns. All are welcome!
                </p>
              </div>
            ) : (
              events.map((event) => {
                const eventDate = new Date(event.event_date);
                const fullDate = eventDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                });
                const isExpanded = expandedEvents[event.id];

                return (
                  <div
                    className="events-card"
                    key={event.id}
                    onClick={() => toggleEventExpansion(event.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="events-title">{event.title}</div>
                    <p><strong>{fullDate}</strong></p>
                    {isExpanded && (
                      <>
                        {event.location && <p>📍 {event.location}</p>}
                        {event.description && <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>}
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt={event.title}
                            className="event-image"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFullscreenImage({ src: event.image_url, alt: event.title });
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Google Calendar Embed */}
          <div className="calendar-container">
            <h3 className="calendar-title">Full Calendar</h3>
            <div className="calendar-wrapper">
              <iframe
                src={`https://calendar.google.com/calendar/embed?src=${encodeURIComponent(process.env.REACT_APP_GOOGLE_CALENDAR_ID)}&ctz=America%2FChicago&mode=MONTH`}
                className="google-calendar"
                title="EaDo Civic Calendar"
                style={{ border: 0 }}
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Resources Section */}
      <div id="resources" className="resources">
        <div className="resources-content">
          <h2 className="section-title">City Resources</h2>
          <p className="section-text">Connect with Houston city officials and access important resources</p>

          <div className="resources-grid">
            <div className="resources-column">
              <h3 className="resources-subtitle">Houston City Officials</h3>
              <div className="resource-links">
                <a href="https://www.houstontx.gov/mayor/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Mayor John Whitmire</strong>
                  <span>Office of the Mayor</span>
                </a>
                <a href="https://www.houstontx.gov/council/i/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Joaquin Martinez</strong>
                  <span>District I Representative</span>
                </a>
              </div>

              <h3 className="resources-subtitle">At-Large Representatives</h3>
              <div className="resource-links">
                <a href="https://www.houstontx.gov/council/1/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Julian Ramirez</strong>
                  <span>At-Large Position 1</span>
                </a>
                <a href="https://www.houstontx.gov/council/2/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Willie Davis</strong>
                  <span>At-Large Position 2</span>
                </a>
                <a href="https://www.houstontx.gov/council/3/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Twila Carter</strong>
                  <span>At-Large Position 3</span>
                </a>
                <a href="https://www.houstontx.gov/council/4/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Alejandra Salinas</strong>
                  <span>At-Large Position 4</span>
                </a>
                <a href="https://www.houstontx.gov/council/5/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Council Member Sallie Alcorn</strong>
                  <span>At-Large Position 5</span>
                </a>
              </div>

              <h3 className="resources-subtitle">City Services</h3>
              <div className="resource-links">
                <a href="https://www.houstontx.gov/311/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Houston 311</strong>
                  <span>Report issues and request services</span>
                </a>
                <a href="https://www.houstontx.gov/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>City of Houston</strong>
                  <span>Official city website</span>
                </a>
                <a href="https://data.houstontx.gov/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Houston Open Data</strong>
                  <span>City of Houston Open Data Resources</span>
                </a>
                <a href="https://www.hcp1.net/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Harris Precinct 1</strong>
                  <span>Harris precinct site</span>
                </a>
                <a href="https://www.hcp2.com/" target="_blank" rel="noopener noreferrer" className="resource-link">
                  <strong>Harris Precinct 2</strong>
                  <span>Harris precinct site</span>
                </a>
              </div>
            </div>

            <div className="resources-column">
              <h3 className="resources-subtitle">District I Boundaries</h3>
              <div className="map-container">
                <img
                  src={districtIMap}
                  alt="Houston City Council District I Map"
                  className="district-map"
                  onClick={() => setFullscreenImage({ src: districtIMap, alt: 'Houston City Council District I Map' })}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <p className="map-caption">
                <a href="https://www.houstontx.gov/council/whoismycm.html" target="_blank" rel="noopener noreferrer">
                  Find your council member →
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div className="map-modal" onClick={() => setFullscreenImage(null)}>
          <div className="map-modal-content">
            <button className="map-close" onClick={() => setFullscreenImage(null)}>×</button>
            <img
              src={fullscreenImage.src}
              alt={`${fullscreenImage.alt} - Full View`}
              className="map-fullscreen"
            />
          </div>
        </div>
      )}

      {/* Mailing List Modal */}
      <MailingListModal
        isOpen={showMailingModal}
        onClose={() => setShowMailingModal(false)}
      />

    </div>
  );
}

export default Home;
